/**
 * 管理画面のサイドメニューを本文左端の開閉タブで左右に開閉する
 *
 * 開閉タブは本文の左端（layouts/admin.blade.php）にあり、閉じるとサイドメニューは幅0で完全に隠れる。
 * 開閉状態は Cookie（admin_sidebar_closed=1）に保存し、レイアウト側で初期表示に使う（ページ移動後もちらつかない）。
 * 切り替えるクラスは各要素の data-open-class / data-closed-class（空白区切り）で受け取る。
 * 開閉で本文の幅が変わるため、アニメーション後に resize イベントを発火し、幅に応じて描画する画面に再計算させる。
 * 下部固定の操作バー等の左端は #admin-layout の --admin-sidebar-width（250px / 0）で決まるため、開閉時に更新する。
 *
 * サイドメニューに data-mobile-drawer がある場合（講師ポータル・キャリコンポータル）、スマホ（md 未満）では引き出し式のメニューとして扱う。
 * 初期表示は閉じ、開閉タブで is-mobile-open クラスを付け外しし、背景（#admin-sidebar-backdrop）を押すと閉じる。スマホの開閉状態は保存しない。
 */
const COOKIE_NAME = 'admin_sidebar_closed'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const SIDEBAR_WIDTH = '250px'
const MOBILE_OPEN_CLASS = 'is-mobile-open'
// Tailwind の md（768px）未満
const MOBILE_MEDIA_QUERY = '(max-width: 767.98px)'

/**
 * data-open-class / data-closed-class に従って開閉状態のクラスを付け替える
 *
 * @param {HTMLElement} element
 * @param {boolean} closed
 * @returns {void}
 */
const apply_state_classes = (element, closed) => {
    const open_classes = (element.dataset.openClass || '').split(' ').filter(Boolean)
    const closed_classes = (element.dataset.closedClass || '').split(' ').filter(Boolean)
    open_classes.forEach((name) => element.classList.toggle(name, !closed))
    closed_classes.forEach((name) => element.classList.toggle(name, closed))
}

/**
 * 開閉状態を Cookie に保存する（開いているときは Cookie を削除する）
 *
 * @param {boolean} closed
 * @returns {void}
 */
const save_state = (closed) => {
    document.cookie = closed
        ? `${COOKIE_NAME}=1; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
        : `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
}

/**
 * 初期化
 *
 * @returns {void}
 */
const init_admin_sidebar_toggle = () => {
    const sidebar = document.getElementById('admin-sidebar')
    const menu = document.getElementById('admin-sidebar-menu')
    const layout = document.getElementById('admin-layout')
    if (sidebar === null || menu === null) return

    const toggle_button = document.getElementById('admin-sidebar-toggle')
    const toggle_icon = document.getElementById('admin-sidebar-toggle-icon')
    if (toggle_button === null || toggle_icon === null) return

    const backdrop = document.getElementById('admin-sidebar-backdrop')
    const mobile_query = window.matchMedia(MOBILE_MEDIA_QUERY)
    const has_mobile_drawer = sidebar.dataset.mobileDrawer !== undefined
    const is_mobile_drawer = () => has_mobile_drawer && mobile_query.matches

    // パソコン（md 以上）の開閉状態（Cookie から描画した初期状態）
    let desktop_closed = toggle_button.getAttribute('aria-expanded') === 'false'

    /**
     * 開閉タブのアイコンと aria-expanded を開閉状態に合わせる
     *
     * @param {boolean} closed
     * @returns {void}
     */
    const sync_toggle = (closed) => {
        apply_state_classes(toggle_icon, closed)
        toggle_button.setAttribute('aria-expanded', closed ? 'false' : 'true')
    }

    /**
     * スマホの引き出し式メニューを開閉する
     *
     * @param {boolean} open
     * @returns {void}
     */
    const set_mobile_open = (open) => {
        sidebar.classList.toggle(MOBILE_OPEN_CLASS, open)
        if (backdrop !== null) backdrop.classList.toggle('hidden', !open)
        sync_toggle(!open)
    }

    // スマホでは閉じた状態から始める
    if (is_mobile_drawer()) set_mobile_open(false)

    toggle_button.addEventListener('click', () => {
        if (is_mobile_drawer()) {
            set_mobile_open(!sidebar.classList.contains(MOBILE_OPEN_CLASS))
            return
        }

        const closed = !desktop_closed
        desktop_closed = closed
        apply_state_classes(sidebar, closed)
        apply_state_classes(menu, closed)
        sync_toggle(closed)
        if (layout !== null) layout.style.setProperty('--admin-sidebar-width', closed ? '0px' : SIDEBAR_WIDTH)
        save_state(closed)
    })

    if (backdrop !== null) backdrop.addEventListener('click', () => set_mobile_open(false))

    // 画面幅がスマホ⇔パソコンで切り替わったら、それぞれの開閉状態の見た目に戻す
    if (has_mobile_drawer) {
        mobile_query.addEventListener('change', () => {
            if (mobile_query.matches) {
                set_mobile_open(false)
            } else {
                sidebar.classList.remove(MOBILE_OPEN_CLASS)
                if (backdrop !== null) backdrop.classList.add('hidden')
                sync_toggle(desktop_closed)
            }
        })
    }

    sidebar.addEventListener('transitionend', (event) => {
        if (event.target === sidebar && event.propertyName === 'width') {
            window.dispatchEvent(new Event('resize'))
        }
    })
}

document.addEventListener('DOMContentLoaded', init_admin_sidebar_toggle)
